import { useEffect, useState } from "react";
import SubscriptionCard from "./SubscriptionCard";
import { SubscriptionPlan } from "./subscriptionData";
import "./subscription.css";
import { getRequest, postRequest } from "../../api/apiServices";
import apiList from "../../api/apiList";
import Loader from "../../utils/Loading";
import Popup from "../Common/Popup";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";

const TRUST_ITEMS = [
  "Cancel anytime",
  "Secure checkout",
  "No hidden fees",
];

const Subscriptions = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [isPlanLoading, setIsPlanLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [showPopup, setShowPopup] = useState(false);

  const mapApiPlanToSubscriptionPlan = (apiPlan: any): SubscriptionPlan => {
    const isStandard = apiPlan.name?.toLowerCase() === "standard";

    return {
      id: apiPlan.slug || apiPlan.planType || apiPlan.id.toString(),
      name: apiPlan.name,
      subscriptionPlanId: apiPlan.subscriptionPlanId || "",
      price: apiPlan.basePrice,
      period: "/mo",
      features: apiPlan.features?.map((feature: string) => ({
        name: feature,
        included: true,
      })),
      buttonText: "Choose Plan",
      ...(isStandard && {
        badge: "Most Popular",
        isPopular: true,
      }),
    };
  };

  const fetchSubscriptionPlans = async () => {
    try {
      setIsPlanLoading(true);
      const response: any = await getRequest(apiList.listOfSubscriptions);
      if (response?.data?.success && Array.isArray(response.data.data)) {
        const mappedPlans = response?.data?.data?.map(
          mapApiPlanToSubscriptionPlan
        );
        setPlans([...mappedPlans]);
      } else {
        console.warn("Invalid or missing plans in response:", response.data);
      }
    } catch (error) {
      console.error("Error fetching subscription plans:", error);
    } finally {
      setIsPlanLoading(false);
    }
  };

  const handlePlanSelection = async (planId: string) => {
    try {
      setLoading(true);
      const result = await postRequest(apiList.checkOut, {
        subscriptionPlanId: planId,
      });
      if (result?.data?.success) {
        window.open(result.data.data.url, "_blank");
      } else {
        console.error("Failed to checkout");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlanClick = (planId: string) => {
    setSelectedPlanId(planId);
    setShowPopup(true);
  };

  const confirmPlanCheckout = async () => {
    if (!selectedPlanId) return;
    await handlePlanSelection(selectedPlanId);
    setShowPopup(false);
    setSelectedPlanId(null);
  };

  useEffect(() => {
    fetchSubscriptionPlans();
  }, []);

  return isPlanLoading || loading ? (
    <div className="centered-loader">
      <Loader />
    </div>
  ) : (
    <>
      <div className="subscription-page" id="subscription-section">
        {/* Hero */}
        <div className="subscription-hero">
          <div className="subscription-hero-badge">
            <VerifiedUserOutlinedIcon sx={{ fontSize: 14 }} />
            Subscription Plans
          </div>
          <h1 className="subscription-hero-title">
            Choose the Right Plan for Your Hiring Needs
          </h1>
          <p className="subscription-hero-subtitle">
            Flexible pricing for startups, recruiters, and growing teams.
            Scale your recruitment with the tools you need.
          </p>
          <div className="subscription-trust-row">
            {TRUST_ITEMS.map((item) => (
              <span key={item} className="subscription-trust-item">
                <span className="subscription-trust-dot" />
                {item}
              </span>
            ))}
          </div>
        </div>

        {/* Plan cards */}
        <div className="subscription-cards-grid">
          {plans?.map((plan) => (
            <SubscriptionCard
              key={plan.id}
              plan={plan}
              onPlanSelect={() => handlePlanClick(plan.subscriptionPlanId)}
              isLoading={loading}
            />
          ))}
        </div>
      </div>

      <Popup
        isOpen={showPopup}
        onClose={() => setShowPopup(false)}
        popupName="Confirm your subscription selection"
        popupContain="Are you sure you want to proceed with this plan checkout?"
        onYes={confirmPlanCheckout}
        onNo={() => setShowPopup(false)}
        yesButtonClass="primary-btn"
        yesButtonText="Yes, Continue"
        noButtonText="Cancel"
      />
    </>
  );
};

export default Subscriptions;
